import { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import type { Theme } from "../lib/theme";

interface Props {
  text: string;
  T: Theme;
  FONT: string;
  size?: number;
}

const MAX_W = 260;
const GAP = 8;      // between the trigger and the bubble
const EDGE = 8;     // keep this much clear of the viewport edge

/**
 * A hover definition that cannot be clipped.
 *
 * The bubble used to be absolutely positioned inside the trigger, which meant
 * any ancestor with overflow:hidden cut it off — and the places that most need
 * a definition are table headers, which sit inside exactly that: a rounded,
 * clipped wrapper. It also ran off the right edge of the window on the last
 * column of a table, where there was nothing to the right to run into.
 *
 * So it renders into document.body instead, positioned fixed against the
 * trigger's measured rect and clamped to the viewport. The arrow tracks the
 * trigger rather than the bubble, so it still points at the right "?" after a
 * clamp has shifted the bubble sideways.
 */
export default function InfoTooltip({ text, T, FONT, size = 11 }: Props) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; arrowX: number; below: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current || !bubbleRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();

    // Prefer above; flip below only when there genuinely isn't room, so the
    // bubble doesn't cover the row the pointer is reading.
    const below = t.top - b.height - GAP < EDGE;
    const top = below ? t.bottom + GAP : t.top - b.height - GAP;

    const centred = t.left + t.width / 2 - b.width / 2;
    const left = Math.min(Math.max(centred, EDGE), window.innerWidth - b.width - EDGE);

    setPos({ left, top, arrowX: t.left + t.width / 2 - left, below });
  }, [visible, text]);

  const show = () => { setPos(null); setVisible(true); };
  const hide = () => { setVisible(false); setPos(null); };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        aria-label={text}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: size + 4, height: size + 4, borderRadius: "50%",
          background: T.textWeak + "30", color: T.textWeak,
          fontSize: size - 1, fontWeight: 700, cursor: "default",
          lineHeight: 1, userSelect: "none", flexShrink: 0, verticalAlign: "middle",
        }}
      >
        ?
      </span>

      {visible && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          style={{
            position: "fixed",
            // Rendered offscreen for one frame while it is measured, so it is
            // never seen in the wrong place first.
            left: pos ? pos.left : -9999,
            top: pos ? pos.top : 0,
            visibility: pos ? "visible" : "hidden",
            background: T.bgModal, border: `1px solid ${T.borderSecondary}`,
            borderRadius: 6, padding: "8px 11px", fontSize: 11, fontFamily: FONT,
            color: T.textSecondary, lineHeight: 1.55, whiteSpace: "pre-wrap",
            maxWidth: MAX_W, width: "max-content", zIndex: 9999,
            boxShadow: "0 6px 20px #0007", pointerEvents: "none", textAlign: "left",
          }}
        >
          {text}
          {pos && (
            <span style={{
              position: "absolute",
              [pos.below ? "bottom" : "top"]: "100%",
              left: Math.min(Math.max(pos.arrowX, 10), MAX_W - 10),
              transform: "translateX(-50%)",
              border: "5px solid transparent",
              [pos.below ? "borderBottomColor" : "borderTopColor"]: T.borderSecondary,
              display: "block", width: 0, height: 0,
            } as React.CSSProperties} />
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
